import Cookies from "universal-cookie";
import { fetchWrapper, history } from "@/_helpers";
import { LifeSafer } from "@/_helpers/BrandConstants";
import { userLogoutSuccess } from "../_redux/actions/accountActions";
import * as apiUtils from "../_api/apiUtils";
import * as identityApi from "../_api/identityApi";
import moment from "moment-timezone";

const baseUrl = process.env.API_URL + "/Users/";
const PORTAL_ID = 10;
const RESET_PORTAL_ID = 10;
const DEFAULT_BRAND_ID = 1;
const cookies = new Cookies();
const resetOptionCache = new Map();
const resetValidationCache = new Map();

export const accountService = {
  login,
  loginCE,
  logout,
  refreshToken,
  register,
  verifyEmail,
  forgotPassword,
  sendResetEmail,
  resetCode,
  resetCodeVerify,
  sendResetCode,
  validateResetCode,
  validateResetToken,
  resetNow,
  resetPassword,
  resetProfilePassword,
  userUpdate,
};

function login(UserName, Password, isPhone, options) {
  return identityApi
    .authenticate(UserName, Password, PORTAL_ID, getBrandId(), isPhone, options)
    .then((response) => {
      if (!response) {
        throw new Error("Authentication failed");
      }

      if (!response.user) {
        throw new Error("User object missing");
      }

      if (!response.tokenGrant) {
        throw new Error("Token missing");
      }

      const tokenGrant = response.tokenGrant;
      const apiUser = response.user;

      const normalizedUser = {
        ...apiUser,

        jwtToken: tokenGrant.accessToken,

        accessToken: tokenGrant.accessToken,

        refreshToken: tokenGrant.refreshToken,

        expiresIn: tokenGrant.expiresIn,

        userId: apiUser.userId,
        userID: apiUser.userId,

        languageID: apiUser.languageId,

        clientID: apiUser.clientID || null,
        clientId: apiUser.clientID || null,
        jurisdictionID:
          apiUser.jurisdictionId || apiUser.jurisdictionID || null,

        phoneNumberExt: apiUser.ext,
      };

      delete normalizedUser.regionIds;
      delete normalizedUser.partnerIds;

      clearUserSession();

      cookies.set("driveSaferIdentity", JSON.stringify(tokenGrant), {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(1, "h").format()),
      });
      cookies.set("driveSaferUser", JSON.stringify(normalizedUser), {
        path: "/",
        sameSite: "strict",
        expires: new Date(moment().add(12, "h").format()), //Cookie expires after 12 hours
      });

      return normalizedUser;
    })
    .catch((error) => {
      clearUserSession();

      throw error;
    });
}

function clearUserSession() {
  cookies.remove("driveSaferIdentity", { path: "/" });
  cookies.remove("driveSaferUser", { path: "/" });

  // sessionStorage.removeItem("driveSaferUser");
}

function loginCE(EnrollmentCode) {
  return fetchWrapper
    .get(`${baseUrl}CEAuthentication?SecretKey=${EnrollmentCode}`)
    .then((user) => {
      if (user.userId !== undefined) {
        // publish user to subscribers and start timer to refresh token
        cookies.set("driveSaferUser", JSON.stringify(user), {
          path: "/",
          sameSite: "strict",
        });
        return user;
      }
      return undefined;
    });
}

function logout() {
  // revoke token, stop refresh timer, publish null to user subscribers and redirect to login page
  //fetchWrapper.post(`${baseUrl}/revoke-token`, {});
  //stopRefreshTokenTimer();
  cookies.remove("driveSaferUser", { path: "/" }); //Remove cookies
  cookies.remove("driveSaferIdentity", { path: "/" }); //Remove cookies
  userLogoutSuccess(); //Remove state objects
  window.Five9SocialWidget.removeWidget(); // remove the chat widget on logout
  history.push("/account/login");
}

function refreshToken() {
  return fetchWrapper.post(`${baseUrl}refresh-token`, {}).then((user) => {
    // start timer to refresh token
    //startRefreshTokenTimer();
    return user;
  });
}

function register(params) {
  return fetchWrapper.post(`${baseUrl}/register`, params);
}

function verifyEmail(token) {
  return fetchWrapper.post(`${baseUrl}/verify-email`, { token });
}

// function forgotPassword(emailAddress) {
//   return fetchWrapper
//     .post(`${baseUrl}ForgotPassword`, { emailAddress })
//     .then((rtn) => {
//       return rtn;
//     });
// }

function forgotPassword(emailObj, isPhone, phoneNumber) {
  const resetRequest = {
    emailAddress: getResetEmail(emailObj),
    phoneNumber: phoneNumber || getResetPhoneNumber(emailObj),
    isPhone,
  };

  return identityApi
    .resetOptions(
      resetRequest.emailAddress,
      RESET_PORTAL_ID,
      isPhone,
      resetRequest.phoneNumber,
    )
    .then((options) => {
      cacheResetOptions(resetRequest, options);
      return normalizeResetOptions(options);
    });
}

function sendResetEmail(emailObj) {
  return generateIdentityResetCode(emailObj, "Email").then((rtn) => {
    return rtn?.message ? rtn : { message: "Sent" };
  });
}

function resetCode(emailObj) {
  return generateIdentityResetCode(emailObj, "SMS");
}

function resetCodeVerify(emailObj) {
  return Promise.resolve().then(() =>
    validateResetCode(getResetUserId(emailObj), emailObj.resetCode).then(
      (info) => normalizeResetCodeResult(info, emailObj),
    ),
  );
}

function validateResetToken(userID, token) {
  return validateResetCode(userID, token).then((info) => {
    if (isResetCodeFailure(info)) {
      throw new Error(info.reason || "Reset code verification failure");
    }

    resetValidationCache.set(getValidationCacheKey(userID, token), info);
    return info;
  });
}

function resetPassword(userID, token, password) {
  return resetNow(userID, password, token).then((info) => {
    if (info?.email) {
      return info;
    }

    return (
      resetValidationCache.get(getValidationCacheKey(userID, token)) || info
    );
  });
}

function resetProfilePassword(userID, password) {
  return apiUtils
    .post(`${baseUrl}ResetUserPassword`, { userID, password })
    .then(apiUtils.handleResponse)
    .catch(apiUtils.handleError);
}

function sendResetCode(resetRequest, brand, mechanism) {
  const phoneNumber = getResetPhoneNumber(resetRequest);
  const isPhone = Boolean(resetRequest?.isPhone || phoneNumber);

  return identityApi.generateResetCode(
    getResetEmail(resetRequest),
    RESET_PORTAL_ID,
    brand,
    mechanism,
    {
      isPhone,
      phoneNumber,
    },
  );
}

function validateResetCode(userId, resetCode) {
  return identityApi.verifyResetCode(userId, resetCode);
}

function resetNow(userId, newPassword, resetCode) {
  return identityApi.resetNow(userId, newPassword, resetCode);
}

function generateIdentityResetCode(emailObj, mechanism) {
  return sendResetCode(
    getResetIdentifier(emailObj),
    getResetBrand(emailObj),
    mechanism,
  );
}

function getResetIdentifier(resetRequest) {
  if (typeof resetRequest === "string") {
    return resetRequest;
  }

  return resetRequest?.emailAddress || resetRequest?.phoneNumber || "";
}

function getResetEmail(resetRequest) {
  if (typeof resetRequest === "string") {
    return resetRequest;
  }

  return resetRequest?.emailAddress || resetRequest?.email || "";
}

function getResetPhoneNumber(resetRequest) {
  if (typeof resetRequest === "string") {
    return "";
  }

  return resetRequest?.phoneNumber || "";
}

function getResetBrand(resetRequest) {
  const brand = resetRequest?.brandIdentifier || resetRequest?.brand;

  if (brand === LifeSafer) {
    return "Lifesafer";
  }

  return brand || "Lifesafer";
}

function getResetCacheKey(resetRequest) {
  return getResetIdentifier(resetRequest).trim().toLowerCase();
}

function cacheResetOptions(resetRequest, options) {
  const userId = getUserIdFromResponse(options);

  if (userId) {
    resetOptionCache.set(getResetCacheKey(resetRequest), userId);
  }
}

function getResetUserId(resetRequest) {
  const userId =
    resetRequest?.userId ||
    resetRequest?.userID ||
    resetOptionCache.get(getResetCacheKey(resetRequest));

  if (!userId) {
    throw new Error("Reset user id is missing.");
  }

  return userId;
}

function getUserIdFromResponse(response) {
  return (
    response?.userId ||
    response?.userID ||
    response?.user?.userId ||
    response?.user?.userID ||
    response?.data?.userId ||
    response?.data?.userID
  );
}

function getResetMechanisms(options) {
  if (Array.isArray(options)) {
    return options
      .map((mechanism) =>
        typeof mechanism === "string"
          ? mechanism
          : mechanism?.mechanism || mechanism?.type || mechanism?.name,
      )
      .map(normalizeResetMechanism)
      .filter(Boolean);
  }

  const mechanismSource =
    options?.mechanisms ||
    options?.resetOptions ||
    options?.options ||
    options?.data?.mechanisms ||
    options?.data?.resetOptions ||
    options?.data?.options;

  if (Array.isArray(mechanismSource)) {
    return mechanismSource
      .map((mechanism) =>
        typeof mechanism === "string"
          ? mechanism
          : mechanism?.mechanism || mechanism?.type || mechanism?.name,
      )
      .map(normalizeResetMechanism)
      .filter(Boolean);
  }

  const mechanisms = [];
  if (options?.email || options?.hasEmail || options?.canEmail) {
    mechanisms.push("Email");
  }
  if (options?.text || options?.sms || options?.hasText || options?.canText) {
    mechanisms.push("SMS");
  }

  return mechanisms;
}

function normalizeResetMechanism(mechanism) {
  const normalizedMechanism = String(mechanism || "").toLowerCase();

  if (normalizedMechanism === "sms" || normalizedMechanism === "text") {
    return "SMS";
  }

  if (normalizedMechanism === "email") {
    return "Email";
  }

  return mechanism;
}

function normalizeResetCodeResult(info, resetRequest) {
  if (isResetCodeFailure(info)) {
    return { ...info, message: "Invalid" };
  }

  return {
    ...info,
    token: info?.token || resetRequest?.resetCode,
    userId: getUserIdFromResponse(info) || getResetUserId(resetRequest),
  };
}

function isResetCodeFailure(info) {
  return (
    info?.message === "Invalid" ||
    info?.reason === "Reset code verification failure"
  );
}

function normalizeResetOptions(options) {
  const resetOptions = Array.isArray(options) ? { options } : options || {};

  return {
    ...resetOptions,
    userId: getUserIdFromResponse(options),
    mechanisms: getResetMechanisms(options),
  };
}

function getValidationCacheKey(userID, token) {
  return `${userID}:${token}`;
}

// function normalizeAuthenticatedUser(user) {
//   if (!user || typeof user !== "object") {
//     return user;
//   }

//   const authenticatedUser = user.user || user.data || user.result || user;

//   return {
//     ...authenticatedUser,
//     jwtToken:
//       authenticatedUser.jwtToken ||
//       authenticatedUser.accessToken ||
//       authenticatedUser.token,
//     userId: authenticatedUser.userId ?? authenticatedUser.userID,
//     userID: authenticatedUser.userID ?? authenticatedUser.userId,
//     clientID: authenticatedUser.clientID ?? authenticatedUser.clientId,
//     clientId: authenticatedUser.clientId ?? authenticatedUser.clientID,
//   };
// }

function getBrandId() {
  const configuredBrandId = getConfiguredBrandId();

  if (configuredBrandId) {
    return configuredBrandId;
  }

  const brandIdentifier = cookies.get("lsEnrollmentReferral") || LifeSafer;
  const brandIds = {
    [LifeSafer]: DEFAULT_BRAND_ID,
  };

  return brandIds[brandIdentifier] || DEFAULT_BRAND_ID;
}

function getConfiguredBrandId() {
  try {
    return Number(process.env.IDEN_BRAND_ID) || null;
  } catch {
    return null;
  }
}

function userUpdate(userObj) {
  return identityApi.updateUser(userObj);
}

// helper functions
// let refreshTokenTimeout;

// function startRefreshTokenTimer() {
//   // parse json object from base64 encoded jwt token
//   //const jwtToken = JSON.parse(atob(userSubject.value.jwtToken.split(".")[1]));
//   // set a timeout to refresh the token a minute before it expires
//   // const expires = new Date(jwtToken.exp * 1000);
//   // const timeout = expires.getTime() - Date.now() - 60 * 1000;
//   // refreshTokenTimeout = setTimeout(refreshToken, timeout);
// }

// function stopRefreshTokenTimer() {
//   clearTimeout(refreshTokenTimeout);
// }
